import React, { useEffect, useMemo, useState } from "react";
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Grid, 
  Button, 
  TextField, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Stack, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Alert, 
  CircularProgress, 
  IconButton, 
  Tooltip,
  Chip,
  Card,
  CardContent
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TodayIcon from "@mui/icons-material/Today";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import PageNavBar from "../components/PageNavBar";
import { listClassrooms, listTimetableEntriesByClassroom, createTimetableEntry, updateTimetableEntry, deleteTimetableEntry } from "../context/Api";
import TimePicker15Min from "../components/TimePicker15Min";

const weekdays = [
  { label: "Sunday", value: 0, short: "Sun", color: "#ea4335" },
  { label: "Monday", value: 1, short: "Mon", color: "#4285f4" },
  { label: "Tuesday", value: 2, short: "Tue", color: "#34a853" },
  { label: "Wednesday", value: 3, short: "Wed", color: "#fbbc04" },
  { label: "Thursday", value: 4, short: "Thu", color: "#ff6d01" },
  { label: "Friday", value: 5, short: "Fri", color: "#9c27b0" },
  { label: "Saturday", value: 6, short: "Sat", color: "#00acc1" },
];

const Timetable = () => {
  const { classroomId: paramClassroomId } = useParams();
  const navigate = useNavigate();
  const { userRole, tenantId, selectedTenantId } = useUserContext();
  const effectiveTenantId = userRole === "SuperAdmin" ? selectedTenantId : tenantId;
  const isAdmin = userRole === "admin" || userRole === "SuperAdmin";

  const [classrooms, setClassrooms] = useState([]);
  const [classroomId, setClassroomId] = useState(paramClassroomId || "");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState({ 
    title: "", 
    weekday: 1, 
    startTime: "09:00", 
    endTime: "10:00",
    startDate: "",
    endDate: ""
  });
  const [saving, setSaving] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (!effectiveTenantId) return;
    (async () => {
      try {
        const list = await listClassrooms(effectiveTenantId);
        setClassrooms(list || []);
        if (!paramClassroomId && list.length > 0) {
          setClassroomId(list[0].id);
        }
      } catch (e) {
        setError(e.message || "Failed to load classrooms");
      }
    })();
  }, [effectiveTenantId, paramClassroomId]);

  useEffect(() => {
    if (!effectiveTenantId || !classroomId) return;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const list = await listTimetableEntriesByClassroom(effectiveTenantId, classroomId);
        setEntries((list || []).filter(e => e.active !== false));
      } catch (e) {
        setError(e.message || "Failed to load timetable");
      } finally {
        setLoading(false);
      }
    })();
  }, [effectiveTenantId, classroomId]);

  // Filter entries based on date range
  const filteredEntries = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return entries.filter(entry => {
      // If no date range, show all entries
      if (!entry.startDate && !entry.endDate) return true;
      
      const startDate = entry.startDate ? new Date(entry.startDate) : null;
      const endDate = entry.endDate ? new Date(entry.endDate) : null;
      
      // Check if current date is within range
      if (startDate && today < startDate) return false;
      if (endDate && today > endDate) return false;
      
      return true;
    });
  }, [entries]);

  const entriesByDay = useMemo(() => {
    const map = new Map();
    weekdays.forEach(w => map.set(w.value, []));
    filteredEntries.forEach(e => {
      const arr = map.get(e.weekday) || [];
      arr.push(e);
      map.set(e.weekday, arr);
    });
    weekdays.forEach(w => {
      const arr = map.get(w.value) || [];
      arr.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
      map.set(w.value, arr);
    });
    return map;
  }, [filteredEntries]);

  // Get entries for a specific date
  const getEntriesForDate = (date) => {
    const dayOfWeek = date.getDay();
    const dayEntries = entriesByDay.get(dayOfWeek) || [];
    
    return dayEntries.filter(entry => {
      if (!entry.startDate && !entry.endDate) return true;
      
      const startDate = entry.startDate ? new Date(entry.startDate) : null;
      const endDate = entry.endDate ? new Date(entry.endDate) : null;
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      
      if (startDate) {
        startDate.setHours(0, 0, 0, 0);
        if (checkDate < startDate) return false;
      }
      if (endDate) {
        endDate.setHours(0, 0, 0, 0);
        if (checkDate > endDate) return false;
      }
      
      return true;
    });
  };

  const resetForm = () => setForm({ 
    title: "", 
    weekday: 1, 
    startTime: "09:00", 
    endTime: "10:00",
    startDate: "",
    endDate: ""
  });

  const openCreate = () => {
    setEditingEntry(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setForm({ 
      title: entry.title, 
      weekday: entry.weekday, 
      startTime: entry.startTime, 
      endTime: entry.endTime,
      startDate: entry.startDate || "",
      endDate: entry.endDate || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!effectiveTenantId || !classroomId) return;
    setSaving(true);
    setError("");
    try {
      if (!form.title || !form.startTime || !form.endTime) throw new Error("Please fill all fields");
      if (form.startTime >= form.endTime) throw new Error("Start must be before end");
      if (form.startDate && form.endDate && form.startDate > form.endDate) {
        throw new Error("Start date must be before end date");
      }
      
      const payload = {
        title: form.title,
        weekday: form.weekday,
        startTime: form.startTime,
        endTime: form.endTime,
        startDate: form.startDate || null,
        endDate: form.endDate || null
      };
      
      if (editingEntry) {
        await updateTimetableEntry(editingEntry.id, payload);
        setSuccess("Event updated");
      } else {
        await createTimetableEntry({ 
          tenantId: effectiveTenantId, 
          classroomId, 
          ...payload
        });
        setSuccess("Event added");
      }
      const list = await listTimetableEntriesByClassroom(effectiveTenantId, classroomId);
      setEntries((list || []).filter(e => e.active !== false));
      setDialogOpen(false);
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    setSaving(true);
    setError("");
    try {
      await deleteTimetableEntry(entry.id);
      const list = await listTimetableEntriesByClassroom(effectiveTenantId, classroomId);
      setEntries((list || []).filter(e => e.active !== false));
      setSuccess("Event deleted");
    } catch (e) {
      setError(e.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  const selectedClassroom = classrooms.find(c => c.id === classroomId);

  // Generate calendar days for current week
  const getWeekDays = () => {
    const week = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      week.push(date);
    }
    return week;
  };

  const weekDays = useMemo(() => getWeekDays(), [currentDate]);

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [h, m] = time.split(":");
    const hour = parseInt(h);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#f8f9fa", py: 2 }}>
      <PageNavBar title="Timetable" backTo="/dashboard" />
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ 
            bgcolor: "#fff", 
            borderBottom: "1px solid #e0e0e0", 
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={goToToday}
                size="small"
              >
                Today
              </Button>
              <IconButton onClick={goToPreviousWeek} size="small">
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={goToNextWeek} size="small">
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 500, ml: 2 }}>
                {weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} - {weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Classroom</InputLabel>
                <Select
                  value={classroomId}
                  label="Classroom"
                  onChange={(e) => {
                    const id = e.target.value;
                    setClassroomId(id);
                    if (paramClassroomId) {
                      navigate(`/timetable/${id}`);
                    }
                  }}
                >
                  {classrooms.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name || c.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isAdmin && (
                <Button variant="contained" onClick={openCreate} disabled={!classroomId} startIcon={<EventIcon />}>
                  Add Event
                </Button>
              )}
            </Stack>
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 2 }} onClose={() => setError("")}>{error}</Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ m: 2 }} onClose={() => setSuccess("")}>{success}</Alert>
          )}

          {!classroomId ? (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <EventIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography color="text.secondary" variant="h6">Select a classroom to view its timetable</Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Week view - Google Calendar style */}
              <Grid container>
                {/* Time column header */}
                <Grid item xs={1.5} sx={{ borderRight: "1px solid #e0e0e0" }}>
                  <Box sx={{ height: 60, borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="caption" color="text.secondary">Time</Typography>
                  </Box>
                </Grid>
                
                {/* Day headers */}
                {weekDays.map((date, idx) => {
                  const isToday = date.toDateString() === new Date().toDateString();
                  const weekday = weekdays[date.getDay()];
                  return (
                    <Grid key={idx} item xs={1.5} sx={{ borderRight: idx < 6 ? "1px solid #e0e0e0" : "none" }}>
                      <Box sx={{ 
                        height: 60, 
                        borderBottom: "1px solid #e0e0e0",
                        bgcolor: isToday ? "#e8f0fe" : "#fff",
                        p: 1,
                        textAlign: "center"
                      }}>
                        <Typography variant="caption" sx={{ color: weekday.color, fontWeight: 600, display: "block" }}>
                          {weekday.short}
                        </Typography>
                        <Typography variant={isToday ? "h6" : "body2"} sx={{ fontWeight: isToday ? 700 : 400 }}>
                          {date.getDate()}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Time slots and events */}
              <Grid container>
                {/* Time column */}
                <Grid item xs={1.5} sx={{ borderRight: "1px solid #e0e0e0" }}>
                  {Array.from({ length: 24 }, (_, i) => (
                    <Box key={i} sx={{ 
                      height: 80, 
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                      pr: 1,
                      pt: 0.5
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {i === 0 ? "12 AM" : i < 12 ? `${i} AM` : i === 12 ? "12 PM" : `${i - 12} PM`}
                      </Typography>
                    </Box>
                  ))}
                </Grid>

                {/* Day columns */}
                {weekDays.map((date, dayIdx) => {
                  const dayEntries = getEntriesForDate(date);
                  const weekday = weekdays[date.getDay()];
                  
                  return (
                    <Grid key={dayIdx} item xs={1.5} sx={{ 
                      borderRight: dayIdx < 6 ? "1px solid #e0e0e0" : "none",
                      position: "relative"
                    }}>
                      {Array.from({ length: 24 }, (_, hour) => (
                        <Box key={hour} sx={{ 
                          height: 80, 
                          borderBottom: "1px solid #f0f0f0",
                          position: "relative"
                        }} />
                      ))}
                      
                      {/* Render events */}
                      {dayEntries.map((entry, entryIdx) => {
                        const [startH, startM] = entry.startTime.split(":").map(Number);
                        const [endH, endM] = entry.endTime.split(":").map(Number);
                        const startMinutes = startH * 60 + startM;
                        const endMinutes = endH * 60 + endM;
                        const top = (startMinutes / 60) * 80;
                        const height = ((endMinutes - startMinutes) / 60) * 80;
                        
                        return (
                          <Card
                            key={entry.id}
                            sx={{
                              position: "absolute",
                              top: `${top}px`,
                              left: "4px",
                              right: "4px",
                              height: `${Math.max(height, 40)}px`,
                              bgcolor: weekday.color,
                              color: "#fff",
                              zIndex: 1,
                              cursor: "pointer",
                              boxShadow: 1,
                              "&:hover": {
                                boxShadow: 4,
                                zIndex: 2,
                                transform: "scale(1.02)",
                                transition: "all 0.2s"
                              }
                            }}
                            onClick={() => isAdmin && openEdit(entry)}
                          >
                            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", fontSize: "0.75rem", color: "#fff", mb: 0.25 }}>
                                  {entry.title}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "#fff", opacity: 0.95, display: "block" }}>
                                  {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
                                </Typography>
                              </Box>
                              {isAdmin && (
                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, justifyContent: "flex-end" }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEdit(entry);
                                    }}
                                    sx={{ 
                                      color: "#000", 
                                      bgcolor: "#fff",
                                      p: 0.5,
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                      "&:hover": {
                                        bgcolor: "#f5f5f5",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                      },
                                      width: 28,
                                      height: 28,
                                      minWidth: 28
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 16, color: "#1976d2" }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(entry);
                                    }}
                                    sx={{ 
                                      color: "#000", 
                                      bgcolor: "#fff",
                                      p: 0.5,
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                      "&:hover": {
                                        bgcolor: "#ffebee",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                      },
                                      width: 28,
                                      height: 28,
                                      minWidth: 28
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
                                  </IconButton>
                                </Stack>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingEntry ? "Edit Event" : "Add Event"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField 
              label="Title" 
              value={form.title} 
              onChange={(e) => setForm({ ...form, title: e.target.value })} 
              fullWidth 
            />
            <FormControl fullWidth>
              <InputLabel>Day</InputLabel>
              <Select 
                label="Day" 
                value={form.weekday} 
                onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}
              >
                {weekdays.map(d => (
                  <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TimePicker15Min
                label="Start Time"
                value={form.startTime}
                onChange={(value) => setForm({ ...form, startTime: value })}
                fullWidth
              />
              <TimePicker15Min
                label="End Time"
                value={form.endTime}
                onChange={(value) => setForm({ ...form, endTime: value })}
                fullWidth
              />
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 1, color: "text.secondary" }}>
              Date Range (Optional - Leave empty to repeat indefinitely)
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="date"
                label="Start Date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="End Date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Timetable;
